import type { Statement } from "./types.ts";
import { generateSIDs } from "./addressing.ts";

/**
 * Deep clone a statement tree.
 */
export function cloneStatement(stmt: Statement): Statement {
  return {
    ...stmt,
    children: stmt.children.map(cloneStatement),
    links: stmt.links.map((l) => ({ ...l, target: { ...l.target } })),
    metadata: { ...stmt.metadata },
  };
}

/**
 * Find the parent of a statement by its SID.
 * Returns { parent, index } or null.
 */
export function findParent(
  root: Statement,
  sid: string
): { parent: Statement; index: number } | null {
  for (let i = 0; i < root.children.length; i++) {
    if (root.children[i]!.sid === sid) {
      return { parent: root, index: i };
    }
    const found = findParent(root.children[i]!, sid);
    if (found) return found;
  }
  return null;
}

/**
 * Find a statement by SID.
 */
export function findStatement(
  root: Statement,
  sid: string
): Statement | null {
  if (root.sid === sid) return root;
  for (const child of root.children) {
    const found = findStatement(child, sid);
    if (found) return found;
  }
  return null;
}

/**
 * Remove a statement from its parent. Returns the removed statement or null.
 */
export function removeStatement(
  root: Statement,
  sid: string
): Statement | null {
  const location = findParent(root, sid);
  if (!location) return null;
  const [removed] = location.parent.children.splice(location.index, 1);
  return removed ?? null;
}

/**
 * Insert a statement after a given SID (as a sibling).
 * If targetSid is "0" (root), appends to root.children.
 */
export function insertAfter(
  root: Statement,
  targetSid: string,
  stmt: Statement
): boolean {
  if (targetSid === "0") {
    root.children.push(stmt);
    generateSIDs(root);
    return true;
  }

  const location = findParent(root, targetSid);
  if (!location) return false;

  location.parent.children.splice(location.index + 1, 0, stmt);
  generateSIDs(root);
  return true;
}

/**
 * Move a statement from one location to after another.
 */
export function moveStatement(
  root: Statement,
  sourceSid: string,
  afterSid: string
): boolean {
  const stmt = removeStatement(root, sourceSid);
  if (!stmt) return false;

  const result = insertAfter(root, afterSid, stmt);
  generateSIDs(root);
  return result;
}

/**
 * Copy a statement to after another location.
 */
export function copyStatement(
  root: Statement,
  sourceSid: string,
  afterSid: string
): boolean {
  const source = findStatement(root, sourceSid);
  if (!source) return false;

  const copy = cloneStatement(source);
  copy.id = crypto.randomUUID();

  const result = insertAfter(root, afterSid, copy);
  generateSIDs(root);
  return result;
}

/**
 * Delete a statement by SID.
 */
export function deleteStatement(root: Statement, sid: string): boolean {
  const removed = removeStatement(root, sid);
  if (!removed) return false;
  generateSIDs(root);
  return true;
}

/**
 * Transpose (swap) two statements.
 */
export function transposeStatements(
  root: Statement,
  sidA: string,
  sidB: string
): boolean {
  const locA = findParent(root, sidA);
  const locB = findParent(root, sidB);
  if (!locA || !locB) return false;

  const stmtA = locA.parent.children[locA.index]!;
  const stmtB = locB.parent.children[locB.index]!;

  locA.parent.children[locA.index] = stmtB;
  locB.parent.children[locB.index] = stmtA;

  generateSIDs(root);
  return true;
}

/**
 * Create a new empty statement.
 */
export function createStatement(content: string = ""): Statement {
  return {
    id: crypto.randomUUID(),
    sid: "",
    content,
    children: [],
    links: [],
    metadata: {
      created: new Date().toISOString(),
      modified: new Date().toISOString(),
    },
  };
}

/**
 * Flatten a statement tree into a list (depth-first, excluding root).
 */
export function flattenTree(root: Statement): Statement[] {
  const result: Statement[] = [];
  function walk(node: Statement): void {
    if (node.sid !== "0") result.push(node);
    for (const child of node.children) walk(child);
  }
  walk(root);
  return result;
}
